import React from "react";
import {
  Dialog,
  Stack,
  Typography,
  Link,
  useTheme,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";

import { useResponsive } from "@app/hooks";
import { FormProvider, RHFTextField } from "@app/components";
import { Strings } from "@app/constants";

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onClickLogIn?: () => void;
  onClickOtpVerificationsDialog?: () => void;
}

const SignUpDialogPopup = ({
  open,
  onClose,
  onClickLogIn,
  onClickOtpVerificationsDialog,
}: LoginDialogProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const methods = useForm();

  const textStyle = (size: number, weight = 400, color?: string) => ({
    fontFamily: "Work Sans",
    fontWeight: weight,
    fontSize: size,
    ...(color && { color }),
  });

  const FormField = ({
    name,
    label,
    placeholder,
  }: {
    name: string;
    label: string;
    placeholder: string;
  }) => (
    <Stack width={isMobile ? "100%" : "82%"}>
      <Typography sx={textStyle(14)}>{label}</Typography>
      <RHFTextField
        fullWidth
        name={name}
        label={label}
        placeholder={placeholder}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root fieldset": { border: "none" },
          backgroundColor: theme.palette.grey[200],
          borderRadius: 1,
        }}
      />
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "55%",
          maxWidth: isMobile ? "100%" : "90%",
          borderRadius: isMobile ? 0 : 3,
          p: isMobile ? 2 : 0,
        },
      }}
    >
      <Stack
        sx={{
          maxHeight: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Stack
          spacing={3}
          sx={{ position: "relative", p: isMobile ? 2 : 4, px: 4 }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Icon
              icon="material-symbols-light:close"
              width="24"
              height="24"
              color={theme.palette.common.black}
            />
          </IconButton>
          <Stack spacing={1} alignItems="center">
            <Typography sx={textStyle(isMobile ? 22 : 28, 700)}>
              {Strings.auth.signUp}
            </Typography>
            <Typography
              textAlign="center"
              sx={textStyle(isMobile ? 14 : 16, 400, theme.palette.grey[800])}
            >
              {"Enter Your Mobile Number To Login"}
            </Typography>
          </Stack>

          <FormProvider methods={methods}>
            <Stack spacing={2} alignItems="center">
              <FormField
                name="phoneNumber"
                label={Strings.authPopup.phoneNumber}
                placeholder={Strings.authPopup.enterPhoneNumber}
              />
              <FormField
                name="email"
                label={Strings.authPopup.emailOptional}
                placeholder={Strings.authPopup.enterEmail}
              />
            </Stack>

            <Stack alignItems="center">
              <LoadingButton
                onClick={onClickOtpVerificationsDialog}
                fullWidth
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "8px",
                  width: isMobile ? "100%" : "65%",
                  py: 1.5,
                  mt: 4,
                }}
              >
                {Strings.button.continue}
              </LoadingButton>
            </Stack>
          </FormProvider>

          <Stack alignItems="center">
            <Typography
              sx={textStyle(isMobile ? 13 : 15, 500, theme.palette.grey[700])}
            >
              {Strings.auth.alreadyHaveIn}
              <Link
                href="#"
                underline="hover"
                onClick={onClickLogIn}
                fontFamily="Work Sans"
                fontWeight={500}
                ml={1}
              >
                {Strings.authPopup.logIn}
              </Link>
            </Typography>
          </Stack>

          <Typography
            textAlign="center"
            sx={[
              textStyle(isMobile ? 11 : 13, 500, theme.palette.grey[700]),
              { px: 4 },
            ]}
          >
            {Strings.authPopup.byContinuingYou}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { SignUpDialogPopup };
